import { getSearchQuery } from "../query.ts";
import { useSearchParams } from "react-router-dom";
import usePaginatedTitleResults from "./usePaginatedTitleResults.ts";
import { CompositeAsset } from "../../../../common/api/decimal/common-types/manageAssets.ts";
import { SearchTitlesExploreData } from "../titles/searchTitlesExplore.ts";
import { useMemo, useState } from "react";
import { SearchTitlesStoreData } from "../titles/searchTitlesStore.ts";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { combineSearchStatus } from "../types.ts";

export type SearchTitlesFilterType =
  | "allResults"
  | "exploreResults"
  | "storeResults";

const SEARCH_CHUNK_SIZE = 10;

export default function useAllTitleResults() {
  const [urlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  const [filter, setFilter] = useState<SearchTitlesFilterType>("allResults");
  const brand = useBrandLoaderData();

  const exploreResults = usePaginatedTitleResults<
    CompositeAsset<"explore" | "favorite">,
    SearchTitlesExploreData
  >({
    query,
    // Ideally we'd check user.hasExplore, but the defer means it starts false and
    // then flips to true, which triggers additional loading and looks bad
    enabled: brand.Explore.enabled,
    route: "/search/api/titles/explore",
    chunkSize: SEARCH_CHUNK_SIZE,
  });

  const storeResults = usePaginatedTitleResults<
    CompositeAsset<"store" | "favorite">,
    SearchTitlesStoreData
  >({
    query,
    enabled: brand.Search.storeSearchEnabled,
    route: "/search/api/titles/store",
    chunkSize: SEARCH_CHUNK_SIZE,
  });

  const interleavedResults = useMemo(() => {
    const storeSizesClone = [...storeResults.sizes];
    const exploreSizesClone = [...exploreResults.sizes];

    // Make sure results appear simultaneously
    if (storeResults.isLoadingMore || exploreResults.isLoadingMore) {
      if (!storeResults.isLoadingMore) {
        storeSizesClone.pop();
      }
      if (!exploreResults.isLoadingMore) {
        exploreSizesClone.pop();
      }
    }

    const totalPages = Math.max(
      storeSizesClone.length,
      exploreSizesClone.length
    );
    const items = [];
    let storeIndex = 0;
    let exploreIndex = 0;
    for (let i = 0; i < totalPages; i++) {
      if (exploreSizesClone[i] && filter !== "storeResults") {
        items.push(
          ...exploreResults.items.slice(
            exploreIndex,
            exploreIndex + exploreSizesClone[i]
          )
        );
        exploreIndex += exploreSizesClone[i];
      }
      if (storeSizesClone[i] && filter !== "exploreResults") {
        items.push(
          ...storeResults.items.slice(
            storeIndex,
            storeIndex + storeSizesClone[i]
          )
        );
        storeIndex += storeSizesClone[i];
      }
    }
    return items;
  }, [storeResults, exploreResults, filter]);

  const filteredTotal = useMemo(() => {
    if (filter === "exploreResults") {
      return exploreResults.totalItems;
    }
    if (filter === "storeResults") {
      return storeResults.totalItems;
    }
    return storeResults.totalItems + exploreResults.totalItems;
  }, [filter, exploreResults, storeResults]);

  const filteredHasMore = useMemo(() => {
    // Don't hide the load more before we have both sets of data,
    // even if some is filtered out
    if (exploreResults.isLoadingMore || storeResults.isLoadingMore) {
      return true;
    }

    if (filter === "exploreResults") {
      return exploreResults.hasMore;
    }
    if (filter === "storeResults") {
      return storeResults.hasMore;
    }
    return storeResults.hasMore || exploreResults.hasMore;
  }, [filter, exploreResults, storeResults]);

  return {
    status: combineSearchStatus(exploreResults.status, storeResults.status),
    items: interleavedResults,
    hasMore: filteredHasMore,
    isLoadingMore: exploreResults.isLoadingMore || storeResults.isLoadingMore,
    loadMore: () => {
      storeResults.loadMore();
      exploreResults.loadMore();
    },
    totalItems: filteredTotal,
    exploreTotal: exploreResults.totalItems,
    storeTotal: storeResults.totalItems,
    filter,
    setFilter: (filter: SearchTitlesFilterType) => {
      setFilter(filter);
    },
  };
}
